import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { COHORT_WRITE_MUTATION_KEY } from '@verantos-dev/data-access-cohorts';

import { projectPageKeys } from '../lib/api/queryKeys';

/**
 * Subscribes to cohort write mutations and invalidates both the project
 * artifact manifest and activity feed when any cohort save/process/discard
 * completes — whether or not the project page routes are the active view.
 *
 * Mounted once in ProjectPage so it stays active across the full project
 * session, including while the user is on the cohort builder sub-route
 * (where the manifest and activity queries are unmounted). When those
 * queries remount on navigation back, TQ sees them as invalidated and
 * refetches immediately.
 */
export const useProjectPageCohortSync = (projectId: string) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    return queryClient.getMutationCache().subscribe((event) => {
      if (
        event.type === 'updated' &&
        event.action.type === 'success' &&
        event.mutation.options.mutationKey?.[0] === COHORT_WRITE_MUTATION_KEY
      ) {
        void queryClient.invalidateQueries({
          queryKey: projectPageKeys.artifactsForProject(projectId),
        });
        void queryClient.invalidateQueries({
          queryKey: projectPageKeys.activityForProject(projectId),
        });
      }
    });
  }, [queryClient, projectId]);
};
